/**
 * Container menus without a DOM: vanilla's AbstractContainerMenu click rules
 * (pickup, right-click split, shift quick-move, double-click gather, drag
 * split, number-key swap, throw, click outside) over a list of slots, plus one
 * builder per container kind that fixes the slot order and the slot rules.
 *
 * Shared by three callers: the UI screens (ui/screens/*, offline and as the
 * prediction in server mode), and the game server (server/actions/inventory.ts)
 * which runs the same click on its own stacks and corrects the client. A menu's
 * slot index is the wire index of a `click`, so the order here is protocol.
 */

import { UK, U_KIND, boxSlots } from './utility/defs';
import { cloneStack, clearStack, emptyStack, enchKey, isEmpty, setStack, type Ench, type Stack } from './types';
import { itemDef, maxStackOf } from './itemdb';
import { I } from './itemIds';
import type { Inventory } from './inventory';
import { consumeCraft, craft } from './recipes';
import { fuelTicks, smeltResult } from './smelting';
import type { FurnaceState } from './furnace';
import { mayPlaceBottle, type BrewingStandState } from './brewing/stand';
import { isBrewIngredient } from './brewing/potions';
import { enchLevel } from './enchant/stack';
import { E, enchantability } from './enchant/registry';
import { rollEnchantments, tableOffers } from './enchant/table';
import { anvilCombine, type AnvilResult } from './enchant/anvil';
import { grindstoneCombine, grindstoneRefund, type GrindstoneResult } from './enchant/grindstone';
import { smithResult } from './smithing';
import {
  BARREL_ID, BREWING_STAND_ID, CRAFTING_TABLE_ID, ENCHANTING_TABLE_ID, GRINDSTONE_ID, SMITHING_TABLE_ID,
  anvilState, chestFacing, isFurnaceBlock,
} from '../blocks';

export interface MenuSlot {
  stack: Stack;
  /** Extra placement rule on top of "not a result slot". */
  mayPlace?: (s: Stack) => boolean;
  /** Return false to pin the current contents (Curse of Binding). */
  mayTake?: (s: Stack) => boolean;
  /** Per-slot stack cap (armor = 1); default = the item's own max. */
  maxStack?: number;
  /** Crafting result: take-only, whole stack at once, excluded from double-click gather. */
  result?: boolean;
  /** Take-only (furnace output): can't place, partial takes allowed. */
  takeOnly?: boolean;
  /** Called after the player removed `taken` from this slot (result slots consume ingredients). */
  onTake?: (taken: Stack) => void;
  /** Called after any change to this slot's contents. */
  onChanged?: () => void;
  /** Creative item-list slot: infinite source, never mutated by clicks. */
  source?: boolean;
  /** Grouping ('main', 'hotbar', 'armor', ...). */
  tag?: string;
}

/** What the click rules need of a container. */
export interface MenuCtx {
  slots: MenuSlot[];
  cursor: Stack;
  inventory: Inventory;
  /** Throw a stack into the world in front of the player. */
  dropStack(s: Stack): void;
  /** Shift-click routing for a non-result slot: move slots[i].stack somewhere sensible. */
  quickMoveStack(i: number): void;
  /** Where a shift-clicked result goes: [start, end, reverse]. */
  resultTarget(): [number, number, boolean];
  clickSource?(slot: MenuSlot, button: number): void;
  quickMoveSource?(slot: MenuSlot): void;
}

export const same = (a: Stack, b: Stack): boolean => a.id === b.id && a.dmg === b.dmg && enchKey(a) === enchKey(b);

export const maxFor = (slot: MenuSlot, s: Stack): number => Math.min(maxStackOf(s.id), slot.maxStack ?? 64);

export function canPlace(slot: MenuSlot, s: Stack): boolean {
  if (slot.result || slot.takeOnly || slot.source) return false;
  return slot.mayPlace ? slot.mayPlace(s) : true;
}

/** Put `count` of `from` into a slot, keeping everything else the stack carries (name, enchantments, repair cost). */
function put(slot: MenuSlot, from: Stack, count: number): void {
  if (count <= 0) clearStack(slot.stack);
  else { setStack(slot.stack, from); slot.stack.count = count; }
}

function touch(slots: Iterable<MenuSlot | undefined>): void {
  for (const s of slots) s?.onChanged?.();
}

/** Give a stack back to the player's inventory, dropping what does not fit. */
export function giveBack(m: MenuCtx, s: Stack): void {
  if (isEmpty(s)) return;
  const trial = cloneStack(s);
  moveInto(m.inventory.slots, trial);
  if (!isEmpty(trial)) m.dropStack(trial);
  clearStack(s);
}

/** Top up matching stacks, then fill empty ones (hotbar first): Inventory.add that keeps names and repair cost. */
function moveInto(stacks: readonly Stack[], s: Stack): void {
  const max = maxStackOf(s.id);
  if (max > 1) {
    for (const t of stacks) {
      if (s.count <= 0) break;
      if (isEmpty(t) || !same(t, s) || t.count >= max) continue;
      const n = Math.min(max - t.count, s.count);
      t.count += n; s.count -= n;
    }
  }
  for (const t of stacks) {
    if (s.count <= 0) break;
    if (!isEmpty(t)) continue;
    const n = Math.min(max, s.count);
    setStack(t, s); t.count = n;
    s.count -= n;
  }
  if (s.count <= 0) clearStack(s);
}

/** vanilla Container#moveItemStackTo over slots[start, end). */
export function moveTo(m: MenuCtx, stack: Stack, start: number, end: number, reverse: boolean): boolean {
  let moved = false;
  const step = reverse ? -1 : 1;
  const begin = reverse ? end - 1 : start;
  const inRange = (i: number): boolean => (reverse ? i >= start : i < end);
  if (maxStackOf(stack.id) > 1) {
    for (let i = begin; inRange(i) && stack.count > 0; i += step) {
      const slot = m.slots[i], t = slot.stack;
      if (isEmpty(t) || !same(t, stack) || slot.source) continue;
      const max = maxFor(slot, stack);
      if (t.count >= max) continue;
      const n = Math.min(max - t.count, stack.count);
      t.count += n; stack.count -= n; moved = true;
      slot.onChanged?.();
    }
  }
  for (let i = begin; inRange(i) && stack.count > 0; i += step) {
    const slot = m.slots[i];
    if (!isEmpty(slot.stack) || !canPlace(slot, stack)) continue;
    const n = Math.min(stack.count, maxFor(slot, stack));
    put(slot, stack, n);
    stack.count -= n; moved = true;
    slot.onChanged?.();
  }
  if (stack.count <= 0) clearStack(stack);
  return moved;
}

/** How many of `s` would fit into slots[start,end) (dry run of moveTo). */
export function roomFor(m: MenuCtx, s: Stack, start: number, end: number): number {
  let room = 0;
  for (let i = start; i < end; i++) {
    const slot = m.slots[i];
    if (slot.source) continue;
    if (isEmpty(slot.stack)) { if (canPlace(slot, s)) room += maxFor(slot, s); }
    else if (same(slot.stack, s)) room += Math.max(0, maxFor(slot, s) - slot.stack.count);
  }
  return room;
}

/** Click outside the panel: drop the cursor stack (left) or one of it (right). */
export function outsideClick(m: MenuCtx, button: number): void {
  const cur = m.cursor;
  if (isEmpty(cur)) return;
  if (button === 0) {
    m.dropStack(cloneStack(cur));
    clearStack(cur);
  } else {
    m.dropStack({ ...cloneStack(cur), count: 1 });
    cur.count -= 1;
    if (cur.count <= 0) clearStack(cur);
  }
}

/** The normal (non-drag) vanilla PICKUP click on a slot. */
export function clickSlot(m: MenuCtx, slot: MenuSlot, button: number): void {
  if (slot.source) { m.clickSource?.(slot, button); return; }
  const cur = m.cursor, st = slot.stack;
  const right = button === 1;
  if (slot.result) {
    if (isEmpty(st)) return;
    if (isEmpty(cur)) {
      setStack(cur, st);
      slot.onTake?.(cloneStack(cur));
    } else if (same(cur, st) && cur.count + st.count <= maxStackOf(cur.id)) {
      cur.count += st.count;
      slot.onTake?.(cloneStack(st));
    }
    slot.onChanged?.();
    return;
  }
  if (!isEmpty(st) && slot.mayTake && !slot.mayTake(st)) return;
  if (isEmpty(st)) {
    if (isEmpty(cur) || !canPlace(slot, cur)) return;
    const n = Math.min(right ? 1 : cur.count, maxFor(slot, cur));
    put(slot, cur, n);
    cur.count -= n;
    if (cur.count <= 0) clearStack(cur);
  } else if (isEmpty(cur)) {
    const n = right ? Math.ceil(st.count / 2) : st.count;
    setStack(cur, st); cur.count = n;
    st.count -= n;
    if (st.count <= 0) clearStack(st);
    slot.onTake?.(cloneStack(cur));
  } else if (canPlace(slot, cur)) {
    if (same(st, cur)) {
      const n = Math.min(right ? 1 : cur.count, maxFor(slot, cur) - st.count);
      if (n > 0) { st.count += n; cur.count -= n; if (cur.count <= 0) clearStack(cur); }
    } else if (cur.count <= maxFor(slot, cur)) {
      const tmp = cloneStack(st);
      setStack(st, cur);
      setStack(cur, tmp);
    }
  } else if (same(st, cur)) {
    // can't place here but it's the same item: top the cursor up from the slot
    const n = Math.min(st.count, maxStackOf(cur.id) - cur.count);
    if (n > 0) {
      cur.count += n; st.count -= n;
      if (st.count <= 0) clearStack(st);
      slot.onTake?.({ ...cloneStack(cur), count: n });
    }
  }
  slot.onChanged?.();
}

/** vanilla PICKUP_ALL: gather matching stacks into the cursor. */
export function pickupAll(m: MenuCtx, clicked: MenuSlot, button: number): void {
  const cur = m.cursor;
  if (isEmpty(cur) || (!isEmpty(clicked.stack) && !clicked.result)) return;
  const max = maxStackOf(cur.id);
  const order = button === 0 ? m.slots : [...m.slots].reverse();
  const touched: MenuSlot[] = [];
  for (let pass = 0; pass < 2; pass++) {
    for (const s of order) {
      if (cur.count >= max) break;
      if (s.result || s.source || isEmpty(s.stack) || !same(s.stack, cur)) continue;
      if (s.mayTake && !s.mayTake(s.stack)) continue;
      if (pass === 0 && s.stack.count === max) continue;
      const n = Math.min(s.stack.count, max - cur.count);
      cur.count += n; s.stack.count -= n;
      if (s.stack.count <= 0) clearStack(s.stack);
      touched.push(s);
    }
  }
  touch(touched);
}

/** Shift-click. */
export function quickMove(m: MenuCtx, slot: MenuSlot): void {
  if (slot.source) { m.quickMoveSource?.(slot); return; }
  if (isEmpty(slot.stack) || (slot.mayTake && !slot.mayTake(slot.stack))) return;
  const idx = m.slots.indexOf(slot);
  if (slot.result) {
    const [a, b, rev] = m.resultTarget();
    const first = cloneStack(slot.stack);
    for (let guard = 0; guard < 640; guard++) {
      if (isEmpty(slot.stack) || !same(slot.stack, first)) break;
      const trial = cloneStack(slot.stack);
      if (roomFor(m, trial, a, b) < trial.count) break;
      moveTo(m, trial, a, b, rev);
      slot.onTake?.(cloneStack(slot.stack));
      if (!slot.onTake) break;
    }
    slot.onChanged?.();
    return;
  }
  // repeat while the slot still holds movable items (vanilla loops the same way)
  for (let guard = 0; guard < 8; guard++) {
    const before = slot.stack.count;
    m.quickMoveStack(idx);
    if (isEmpty(slot.stack) || slot.stack.count === before) break;
  }
  slot.onChanged?.();
}

/** Can `slot` join a drag of the cursor stack? */
export function dragAccepts(m: MenuCtx, slot: MenuSlot): boolean {
  return !slot.result && !slot.source && canPlace(slot, m.cursor) && (isEmpty(slot.stack) || same(slot.stack, m.cursor));
}

/** What a drag over `slots` would leave in each slot and on the cursor. */
export function dragPlan<T extends MenuSlot>(m: MenuCtx, slots: readonly T[], button: number): { counts: Map<T, number>; remaining: number } {
  const counts = new Map<T, number>();
  let remaining = m.cursor.count;
  const each = button === 0 ? Math.floor(m.cursor.count / slots.length) : 1;
  for (const s of slots) {
    const have = isEmpty(s.stack) ? 0 : s.stack.count;
    const target = Math.min(have + each, maxFor(s, m.cursor));
    counts.set(s, target);
    remaining -= target - have;
  }
  return { counts, remaining };
}

/** vanilla QUICK_CRAFT (drag-split): left = even split, right = one each. */
export function finishDrag(m: MenuCtx, slots: readonly MenuSlot[], button: number): void {
  const plan = dragPlan(m, slots, button);
  for (const [s, n] of plan.counts) put(s, m.cursor, n);
  m.cursor.count = plan.remaining;
  if (m.cursor.count <= 0) clearStack(m.cursor);
  touch(slots);
}

/** Number key over a slot: swap it with the hotbar stack `hb` (or the offhand). */
export function swapSlot(m: MenuCtx, slot: MenuSlot, hb: Stack): void {
  if (slot.source || slot.stack === hb) return;
  const s = slot.stack;
  if (isEmpty(s) && isEmpty(hb)) return;
  if (!isEmpty(s) && slot.mayTake && !slot.mayTake(s)) return;
  if (slot.result) {
    if (isEmpty(s)) return;
    const got = cloneStack(s);
    if (isEmpty(hb)) setStack(hb, got);
    else if (m.inventory.canAdd(got.id, got.count, got.dmg, got.ench)) moveInto(m.inventory.slots, cloneStack(got));
    else return;
    slot.onTake?.(got);
  } else if (isEmpty(hb)) {
    setStack(hb, s);
    clearStack(s);
  } else if (isEmpty(s)) {
    if (!canPlace(slot, hb)) return;
    const k = Math.min(hb.count, maxFor(slot, hb));
    put(slot, hb, k);
    hb.count -= k;
    if (hb.count <= 0) clearStack(hb);
  } else if (canPlace(slot, hb) && hb.count <= maxFor(slot, hb)) {
    const tmp = cloneStack(s);
    setStack(s, hb);
    setStack(hb, tmp);
  }
  slot.onChanged?.();
}

/** Q over a slot: drop one (whole: the stack). */
export function throwSlot(m: MenuCtx, slot: MenuSlot, whole: boolean): boolean {
  if (slot.source || isEmpty(slot.stack)) return false;
  if (slot.mayTake && !slot.mayTake(slot.stack)) return false;
  if (slot.result) {
    const first = cloneStack(slot.stack);
    for (let g = 0; g < (whole ? 64 : 1); g++) {
      if (isEmpty(slot.stack) || !same(slot.stack, first)) break;
      const got = cloneStack(slot.stack);
      m.dropStack(got);
      slot.onTake?.(cloneStack(got));
      if (!slot.onTake) break;
    }
  } else {
    const n = whole ? slot.stack.count : 1;
    m.dropStack({ ...cloneStack(slot.stack), count: n });
    slot.stack.count -= n;
    if (slot.stack.count <= 0) clearStack(slot.stack);
  }
  slot.onChanged?.();
  return true;
}

// ---------------------------------------------------------------- clicks on the wire ----

/** `click.mode` values. 0..5 are vanilla's; the rest are this game's. */
export const CLICK = {
  PICKUP: 0, QUICK_MOVE: 1, SWAP: 2, CLONE: 3, THROW: 4, DRAG: 5, PICKUP_ALL: 6,
  /** A menu button: enchanting option `btn`, anvil rename (`text`). */
  BUTTON: 7,
  /** Creative: set inventory slot `slot` (window-0 wire index, -1 = cursor, -999 = throw) to `item`. */
  CREATIVE_SET: 8,
  /** Recipe book: refill the crafting grid with `grid` (item id per cell, 0 = empty), `btn` of each. */
  FILL: 9,
} as const;
/** `click.slot` for a click outside the panel. */
export const SLOT_OUTSIDE = -999;
/** `click.btn` of a SWAP with the offhand. */
export const BTN_OFFHAND = 40;

export interface ClickArgs { slot: number; btn: number; mode: number; slots?: readonly number[]; text?: string; grid?: readonly number[] }

/**
 * Run one click as the wire describes it. False = the click is not valid for
 * this menu (bad slot, bad mode): nothing was changed and the sender is out of
 * step. A valid click that happens to do nothing is still true.
 */
export function applyClick(m: Menu, c: ClickArgs): boolean {
  const { btn, mode } = c;
  if (!Number.isInteger(btn) || !Number.isInteger(mode) || !Number.isInteger(c.slot)) return false;
  if (mode === CLICK.BUTTON) return m.button ? m.button(btn, typeof c.text === 'string' ? c.text : undefined) : false;
  if (mode === CLICK.FILL) return m.fill ? m.fill(c.grid ?? [], btn) : false;
  if (c.slot === SLOT_OUTSIDE) {
    if (mode !== CLICK.PICKUP || (btn !== 0 && btn !== 1)) return false;
    outsideClick(m, btn);
    return true;
  }
  if (mode === CLICK.DRAG) {
    const idx = c.slots;
    if (!Array.isArray(idx) || idx.length < 2 || idx.length > m.slots.length || (btn !== 0 && btn !== 1)) return false;
    if (isEmpty(m.cursor) || m.cursor.count < idx.length || new Set(idx).size !== idx.length) return false;
    const list: MenuSlot[] = [];
    for (const i of idx) {
      const s = Number.isInteger(i) ? m.slots[i] : undefined;
      if (!s || !dragAccepts(m, s)) return false;
      list.push(s);
    }
    finishDrag(m, list, btn);
    return true;
  }
  const slot = m.slots[c.slot];
  if (!slot || slot.source) return false;
  switch (mode) {
    case CLICK.PICKUP: if (btn !== 0 && btn !== 1) return false; clickSlot(m, slot, btn); return true;
    case CLICK.QUICK_MOVE: quickMove(m, slot); return true;
    case CLICK.SWAP: {
      const hb = btn === BTN_OFFHAND ? m.inventory.offhand : btn >= 0 && btn < 9 ? m.inventory.slots[btn] : null;
      if (!hb) return false;
      swapSlot(m, slot, hb);
      return true;
    }
    case CLICK.THROW: if (btn !== 0 && btn !== 1) return false; throwSlot(m, slot, btn === 1); return true;
    case CLICK.PICKUP_ALL: if (btn !== 0 && btn !== 1) return false; pickupAll(m, slot, btn); return true;
    default: return false;
  }
}

// ------------------------------------------------------------------------- menus ----

export interface MenuEnv {
  inventory: Inventory;
  creative: () => boolean;
  dropStack: (s: Stack) => void;
  addXp: (n: number) => void;
  level: () => number;
  spendLevels: (n: number) => void;
  rnd: () => number;
}

export interface Menu extends MenuCtx {
  kind: string;
  /** Slots [0, own) belong to the container (grid, result, chest...); the rest are the player's. */
  own: number;
  /** The player walked away / pressed Esc: temporary inputs go back to the inventory. */
  close(): void;
  /** Work that follows any click (furnace XP owed for an output emptied by shift-click). */
  settle?(): void;
  /** Recompute derived slots (results) from the inputs. */
  recompute?(): void;
  button?(id: number, text?: string): boolean;
  fill?(grid: readonly number[], each: number): boolean;
}

function base(env: MenuEnv, kind: string): Menu {
  return {
    kind, own: 0, slots: [], cursor: emptyStack(), inventory: env.inventory,
    dropStack: (s) => { env.dropStack(s); },
    quickMoveStack: () => { /* per kind */ },
    resultTarget: () => [0, 0, false],
    close: () => { /* per kind */ },
  };
}

/** 27 main (inventory.slots 9..35) then the 9 hotbar slots, as every container lists them. */
function addPlayer(m: Menu): void {
  const inv = m.inventory;
  if (m.own === 0) m.own = m.slots.length;
  for (let i = 9; i < 36; i++) m.slots.push({ stack: inv.slots[i], tag: 'main' });
  for (let i = 0; i < 9; i++) m.slots.push({ stack: inv.slots[i], tag: 'hotbar' });
}

/** Standard routing between the main block and the hotbar for a player slot at `i` (main starts at `p`). */
function mainHotbar(m: Menu, i: number, p: number): void {
  const s = m.slots[i].stack;
  if (i < p + 27) moveTo(m, s, p + 27, p + 36, false); else moveTo(m, s, p, p + 27, false);
}

export interface CraftMenu extends Menu { grid: Stack[]; result: Stack; gw: number; gh: number }

function addCrafting(m: CraftMenu, w: number, h: number): void {
  m.gw = w; m.gh = h;
  m.recompute = () => {
    const r = craft(m.grid, w, h);
    if (r) setStack(m.result, r); else clearStack(m.result);
  };
  m.slots.push({ stack: m.result, result: true, onTake: () => { consumeCraft(m.grid, w, h); m.recompute!(); } });
  for (let i = 0; i < w * h; i++) {
    const st = emptyStack();
    m.grid.push(st);
    m.slots.push({ stack: st, onChanged: () => { m.recompute!(); }, tag: 'grid' });
  }
  m.close = () => { for (const s of m.grid) giveBack(m, s); m.recompute!(); };
  // recipe book: everything in the grid goes back, then `each` of every listed item comes out of the inventory
  m.fill = (grid, each) => {
    if (grid.length !== w * h || !Number.isInteger(each) || each < 1 || each > 64) return false;
    if (!grid.every((id) => Number.isInteger(id) && id >= 0 && (id === 0 || each <= maxStackOf(id)))) return false;
    for (const s of m.grid) giveBack(m, s);
    const need = new Map<number, number>();
    for (const id of grid) if (id) need.set(id, (need.get(id) ?? 0) + each);
    let ok = true;
    for (const [id, n] of need) {
      let have = 0;
      for (const s of m.inventory.slots) if (!isEmpty(s) && s.id === id && !s.ench?.length) have += s.count;
      if (have < n) ok = false;
    }
    if (ok) grid.forEach((id, i) => {
      if (!id) return;
      m.inventory.remove(id, each);
      setStack(m.grid[i], { id, count: each, dmg: 0 });
    });
    m.recompute!();
    return true;
  };
}

/** Window 0: 0 result, 1..4 craft grid, 5..8 armour, 9..35 main, 36..44 hotbar, 45 offhand. */
export function inventoryMenu(env: MenuEnv): CraftMenu {
  const m = Object.assign(base(env, 'inventory'), { grid: [] as Stack[], result: emptyStack(), gw: 2, gh: 2 });
  const inv = env.inventory;
  addCrafting(m, 2, 2);
  for (let i = 0; i < 4; i++) {
    m.slots.push({
      stack: inv.armor[i], maxStack: 1, tag: 'armor',
      mayPlace: (s) => itemDef(s.id).armor?.slot === i,
      // Curse of Binding: armor stays on in survival
      mayTake: (s) => env.creative() || !enchLevel(s, E.BINDING_CURSE),
    });
  }
  m.own = 5;
  addPlayer(m);
  m.slots.push({ stack: inv.offhand, tag: 'offhand' });
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i >= 1 && i <= 8) { moveTo(m, s, 9, 45, false); return; }
    if (i >= 9 && i <= 44) {
      const a = itemDef(s.id).armor;
      if (a && isEmpty(m.slots[5 + a.slot].stack) && moveTo(m, s, 5 + a.slot, 6 + a.slot, false)) return;
      mainHotbar(m, i, 9);
      return;
    }
    if (i === 45) moveTo(m, s, 9, 45, false);
  };
  m.resultTarget = () => [9, 45, true];
  return m;
}

/** Crafting table: 0 result, 1..9 grid, 10..36 main, 37..45 hotbar. */
export function craftingMenu(env: MenuEnv): CraftMenu {
  const m = Object.assign(base(env, 'crafting'), { grid: [] as Stack[], result: emptyStack(), gw: 3, gh: 3 });
  addCrafting(m, 3, 3);
  addPlayer(m);
  m.quickMoveStack = (i) => {
    if (i >= 1 && i <= 9) moveTo(m, m.slots[i].stack, 10, 46, false);
    else if (i >= 10) mainHotbar(m, i, 10);
  };
  m.resultTarget = () => [10, 46, true];
  return m;
}

/** Furnace: 0 input, 1 fuel, 2 output, 3..29 main, 30..38 hotbar. */
export function furnaceMenu(env: MenuEnv, state: FurnaceState): Menu {
  const m = base(env, 'furnace');
  // vanilla: whole points are paid, the fraction is a chance of one more
  const pay = (x: number): void => {
    const n = Math.floor(x) + (env.rnd() < x - Math.floor(x) ? 1 : 0);
    if (n > 0) env.addXp(n);
  };
  m.slots.push({ stack: state.input, mayPlace: (s) => smeltResult(s.id) !== null, tag: 'input' });
  m.slots.push({ stack: state.fuel, mayPlace: (s) => fuelTicks(s.id) > 0, tag: 'fuel' });
  m.slots.push({ stack: state.output, takeOnly: true, tag: 'output', onTake: (t) => { pay(state.claimXp(t.count)); } });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 2) { moveTo(m, s, 3, 39, false); return; }
    if (smeltResult(s.id)) { moveTo(m, s, 0, 1, false); return; }
    if (fuelTicks(s.id) > 0) { moveTo(m, s, 1, 2, false); return; }
    mainHotbar(m, i, 3);
  };
  m.resultTarget = () => [3, 39, true];
  // output emptied by a path that fires no onTake: pay what is still owed
  m.settle = () => { if (state.xpItems > 0 && isEmpty(state.output)) pay(state.claimXp(state.xpItems)); };
  return m;
}

/** Chest / barrel: the container's slots, then 27 main and 9 hotbar. */
export function chestMenu(env: MenuEnv, contents: Stack[], size = contents.length): Menu {
  const m = base(env, 'chest');
  const n = Math.max(9, Math.ceil(size / 9) * 9);
  for (let i = 0; i < n; i++) m.slots.push({ stack: contents[i] ?? (contents[i] = emptyStack()), tag: 'chest' });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i < n) moveTo(m, s, n, n + 36, true); else moveTo(m, s, 0, n, false);
  };
  return m;
}

/** Brewing stand: 0..2 bottles, 3 ingredient, 4 blaze powder, 5..31 main, 32..40 hotbar. */
export function brewingMenu(env: MenuEnv, state: BrewingStandState): Menu {
  const m = base(env, 'brewing');
  for (const b of state.bottles) m.slots.push({ stack: b, maxStack: 1, mayPlace: mayPlaceBottle, tag: 'bottle' });
  m.slots.push({ stack: state.ingredient, mayPlace: (s) => isBrewIngredient(s.id), tag: 'ingredient' });
  m.slots.push({ stack: state.fuel, mayPlace: (s) => s.id === I.BLAZE_POWDER, tag: 'fuel' });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 4) { moveTo(m, s, 5, 41, true); return; }
    if (s.id === I.BLAZE_POWDER && moveTo(m, s, 4, 5, false)) return;
    if (isBrewIngredient(s.id) && moveTo(m, s, 3, 4, false)) return;
    if (mayPlaceBottle(s) && moveTo(m, s, 0, 3, false)) return;
    mainHotbar(m, i, 5);
  };
  return m;
}

export interface EnchantTable {
  bookshelves: () => number;
  /** Player enchantment seed (vanilla xpSeed). */
  seed: () => number;
  rerollSeed: () => void;
}
export interface EnchantMenu extends Menu {
  item: Stack; lapis: Stack;
  costs: [number, number, number];
  clues: (Ench | null)[];
  usable(i: number): boolean;
  recompute(): void;
  /** Called after every recompute (the screen repaints its buttons). */
  onOffers?: () => void;
}

/** Enchanting table: 0 item, 1 lapis, 2..28 main, 29..37 hotbar; button i = option i. */
export function enchantMenu(env: MenuEnv, t: EnchantTable): EnchantMenu {
  const m: EnchantMenu = Object.assign(base(env, 'enchant'), {
    item: emptyStack(), lapis: emptyStack(), costs: [0, 0, 0] as [number, number, number], clues: [null, null, null] as (Ench | null)[],
    usable: (i: number): boolean => {
      const c = m.costs[i];
      if (!(c > 0)) return false;
      if (env.creative()) return true;
      return env.level() >= c && env.level() >= i + 1 && m.lapis.count >= i + 1;
    },
    recompute: (): void => {
      const it = m.item;
      if (isEmpty(it) || it.ench?.length) { m.costs = [0, 0, 0]; m.clues = [null, null, null]; }
      else ({ costs: m.costs, clues: m.clues } = tableOffers(t.seed(), t.bookshelves(), it.id));
      m.onOffers?.();
    },
  });
  const ok = (s: Stack): boolean => enchantability(s.id) > 0 && !s.ench?.length;
  m.slots.push({ stack: m.item, maxStack: 1, tag: 'item', onChanged: () => { m.recompute(); }, mayPlace: ok });
  m.slots.push({ stack: m.lapis, tag: 'lapis', mayPlace: (s) => s.id === I.LAPIS_LAZULI, onChanged: () => { m.recompute(); } });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 1) { moveTo(m, s, 2, 38, true); return; }
    if (s.id === I.LAPIS_LAZULI) { if (moveTo(m, s, 1, 2, false)) return; }
    if (isEmpty(m.item) && ok(s) && s.count === 1) { moveTo(m, s, 0, 1, false); return; }
    mainHotbar(m, i, 2);
  };
  m.button = (i) => {
    if (i !== 0 && i !== 1 && i !== 2) return false;
    m.recompute();
    if (!m.usable(i)) return false;
    const it = m.item;
    const list = rollEnchantments(t.seed(), i, it.id, m.costs[i]);
    if (!list.length) return false;
    if (it.id === I.BOOK) it.id = I.ENCHANTED_BOOK;
    it.ench = list.map((e) => ({ ...e }));
    if (!env.creative()) {
      // vanilla: the cost shown is the requirement; only (slot+1) levels and lapis are spent
      env.spendLevels(i + 1);
      m.lapis.count -= i + 1;
      if (m.lapis.count <= 0) clearStack(m.lapis);
    }
    t.rerollSeed();
    m.recompute();
    return true;
  };
  m.close = () => { giveBack(m, m.item); giveBack(m, m.lapis); };
  return m;
}

export interface AnvilMenu extends Menu {
  left: Stack; right: Stack; result: Stack;
  res: AnvilResult | null;
  /** Name field contents; undefined until an item is placed. */
  text: string | undefined;
  affordable(): boolean;
  recompute(): void;
  onResult?: () => void;
}

/** Anvil: 0 left, 1 right, 2 result, 3..29 main, 30..38 hotbar; button 0 + text = rename. */
export function anvilMenu(env: MenuEnv, onUse?: () => void): AnvilMenu {
  const m: AnvilMenu = Object.assign(base(env, 'anvil'), {
    left: emptyStack(), right: emptyStack(), result: emptyStack(), res: null as AnvilResult | null, text: undefined as string | undefined,
    affordable: (): boolean => !!m.res && !m.res.tooExpensive && (env.creative() || env.level() >= m.res.cost),
    recompute: (): void => {
      if (isEmpty(m.left)) m.text = undefined;
      else if (m.text === undefined) m.text = m.left.name ?? itemDef(m.left.id).name;
      const def = isEmpty(m.left) ? '' : itemDef(m.left.id).name;
      const rename = m.text === undefined ? undefined : m.text === def ? '' : m.text;
      m.res = anvilCombine(m.left, m.right, env.creative(), rename);
      if (m.res && m.affordable()) setStack(m.result, m.res.out); else clearStack(m.result);
      m.onResult?.();
    },
  });
  m.slots.push({ stack: m.left, tag: 'left', onChanged: () => { m.recompute(); } });
  m.slots.push({ stack: m.right, tag: 'right', onChanged: () => { m.recompute(); } });
  m.slots.push({
    stack: m.result, result: true,
    // result taken: pay and consume the inputs
    onTake: () => {
      const r = m.res;
      if (!r) return;
      if (!env.creative()) env.spendLevels(r.cost);
      clearStack(m.left);
      m.text = undefined;
      onUse?.();
      m.right.count -= r.rightUsed;
      if (m.right.count <= 0) clearStack(m.right);
      m.recompute();
    },
  });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 1) { moveTo(m, s, 3, 39, true); return; }
    if (isEmpty(m.left)) { moveTo(m, s, 0, 1, false); return; }
    if (isEmpty(m.right)) { moveTo(m, s, 1, 2, false); return; }
    mainHotbar(m, i, 3);
  };
  m.resultTarget = () => [3, 39, true];
  m.button = (_id, text) => {
    if (text === undefined || m.text === undefined) return false;
    m.text = text.replace(/[\u0000-\u001f\u007f§]/g, '').slice(0, 50);
    m.recompute();
    return true;
  };
  // the player's level can change while open (XP orbs)
  m.settle = () => { if (!!(m.res && m.affordable()) !== !isEmpty(m.result)) m.recompute(); };
  m.close = () => { giveBack(m, m.left); giveBack(m, m.right); clearStack(m.result); m.text = undefined; m.res = null; };
  return m;
}

/** Grindstone: 0 top, 1 bottom, 2 result, 3..29 main, 30..38 hotbar. */
export function grindstoneMenu(env: MenuEnv, onUse?: () => void): Menu {
  const m = base(env, 'grindstone');
  const top = emptyStack(), bot = emptyStack(), result = emptyStack();
  let res: GrindstoneResult | null = null;
  const recompute = (): void => {
    res = grindstoneCombine(top, bot);
    if (res) setStack(result, res.out); else clearStack(result);
  };
  m.recompute = recompute;
  m.slots.push({ stack: top, tag: 'top', onChanged: recompute });
  m.slots.push({ stack: bot, tag: 'bottom', onChanged: recompute });
  m.slots.push({
    stack: result, result: true,
    // result taken: consume both inputs, refund XP
    onTake: () => {
      if (!res) return;
      env.addXp(grindstoneRefund(res.xp, env.rnd));
      clearStack(top);
      clearStack(bot);
      onUse?.();
      recompute();
    },
  });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 1) { moveTo(m, s, 3, 39, true); return; }
    if (isEmpty(top)) { moveTo(m, s, 0, 1, false); return; }
    if (isEmpty(bot)) { moveTo(m, s, 1, 2, false); return; }
    mainHotbar(m, i, 3);
  };
  m.resultTarget = () => [3, 39, true];
  m.close = () => { giveBack(m, top); giveBack(m, bot); clearStack(result); res = null; };
  return m;
}

/** Smithing table: 0 template, 1 base, 2 addition, 3 result, 4..30 main, 31..39 hotbar. */
export function smithingMenu(env: MenuEnv): Menu {
  const m = base(env, 'smithing');
  const tpl = emptyStack(), gear = emptyStack(), add = emptyStack(), out = emptyStack();
  const recompute = (): void => {
    const r = smithResult(tpl, gear, add);
    if (r) setStack(out, r); else clearStack(out);
  };
  m.recompute = recompute;
  m.slots.push({ stack: tpl, onChanged: recompute, mayPlace: (s) => s.id === I.NETHERITE_UPGRADE_SMITHING_TEMPLATE });
  m.slots.push({ stack: gear, onChanged: recompute, maxStack: 1 });
  m.slots.push({ stack: add, onChanged: recompute, mayPlace: (s) => s.id === I.NETHERITE_INGOT });
  m.slots.push({
    stack: out, result: true,
    onTake: () => {
      for (const s of [tpl, gear, add]) { s.count--; if (s.count <= 0) clearStack(s); }
      recompute();
    },
  });
  addPlayer(m);
  m.quickMoveStack = (i) => {
    const s = m.slots[i].stack;
    if (i <= 2) { moveTo(m, s, 4, 40, true); return; }
    if (s.id === I.NETHERITE_UPGRADE_SMITHING_TEMPLATE && moveTo(m, s, 0, 1, false)) return;
    if (s.id === I.NETHERITE_INGOT && moveTo(m, s, 2, 3, false)) return;
    if (isEmpty(gear) && s.count === 1 && moveTo(m, s, 1, 2, false)) return;
    mainHotbar(m, i, 4);
  };
  m.resultTarget = () => [4, 40, true];
  m.close = () => { for (const s of [tpl, gear, add]) giveBack(m, s); clearStack(out); };
  return m;
}

// ---------------------------------------------------------------- container blocks ----

/** The window a right-click on this block opens (null: the block has none). The kind is `open.kind` on the wire. */
export function containerKind(id: number): string | null {
  if (chestFacing(id) >= 0 || id === BARREL_ID || boxSlots(id) > 0) return 'chest';
  if (U_KIND[id] === UK.ENDER) return 'ender';
  if (isFurnaceBlock(id)) return 'furnace';
  if (id === BREWING_STAND_ID) return 'brewing';
  if (id === CRAFTING_TABLE_ID) return 'crafting';
  if (id === ENCHANTING_TABLE_ID) return 'enchant';
  if (anvilState(id)) return 'anvil';
  if (id === GRINDSTONE_ID) return 'grindstone';
  if (id === SMITHING_TABLE_ID) return 'smithing';
  return null;
}
